import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Tabs,
	Typography,
	Alert,
} from 'antd';
import {
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	ArrowLeftOutlined,
	DownloadOutlined,
} from '@ant-design/icons';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import { useModals } from '@/components/modals/ModalProvider';
import dayjs from 'dayjs';
import { formatCurrency, formatCurrencyWithDot } from '@/libs/antd/formater';
import { NAPNJFModal, useNAPNJFModal } from '../../nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '../../nap/NAPJFDownloadPage';
import KolektabilitasModal, {
	useKolektabilitasModal,
} from '@/pages/mpp/KolektabilitasModal';
import { downloadDocumentsAsZip } from '@/utils/zip-files';
import {
	PrintDisbursementSubmitDocumentPayload,
	PrintDisbursementSubmitVerificationPayload,
	downloadDisbursementDocument,
	useDownloadDocumentMutation,
	usePrintDisbursementSubmitDocumentMutation,
	usePrintDisbursementSubmitVerificationMutation,
	usePrintDisbursementVerificationDetailQuery,
} from '@/libs/fleet-api/services/print-disbursement';
import rule, { rules } from '@/libs/antd/rules';
import { useApp } from '@/App';
import SimulationResultModal from '@/components/modals/SimulationResultModal';
import { useUserData } from '@/libs/fleet-api/hooks';
import Checkbox from 'antd/es/checkbox/Checkbox';
import CovenantModal, { CovenantData, useCovenantModal } from './CovenantModal';
import { useSelectAddress } from '@/libs/fleet-api/hooks/master-data';
import {
	AddressMasterData,
	MasterData,
} from '@/libs/fleet-api/services/master-data';
import { MPPDetail } from '@/libs/fleet-api/services/mpp';
import { usePrintPurchaseOrder } from '@/libs/fleet-api/hooks/purchase-order';

const covenantStatusOptions = [
	{
		id: 'done',
		value: 'Done',
	},
	{
		id: 'pending',
		value: 'Pending',
	},
] as unknown as MasterData[];

const DisbursementPage: FC<{
	mppId: number;
	purchaseOrderIds?: number[];
	financingProductId?: number;
	viewOnly?: boolean;
	onBack?: () => void;
}> = ({ mppId, purchaseOrderIds, financingProductId, viewOnly, onBack }) => {
	const { navigate, notification } = useApp();
	const userData = useUserData();
	const { groupName } = userData ?? {};
	const { modal } = useModals();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openPreviewModal,
		openDocumentModal,
		openPreviewDocumentModal,
	} = useModals();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();
	const covenantModal = useCovenantModal();
	const kolektabilitasModal = useKolektabilitasModal();

	// Simulation modal state
	const [simulationModalOpen, setSimulationModalOpen] = useState(false);
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);
	const [selectedSimulation, setSelectedSimulation] = useState<{
		simulationId?: number;
		spfId?: number;
		facilityId?: number;
		calculationTypeId?: number;
	} | null>(null);

	const handleCloseSimulationModal = () => {
		setSimulationModalOpen(false);
		setSelectedSimulation(null);
	};

	const { data, isFetching } = usePrintDisbursementVerificationDetailQuery({
		mppId,
		financingProductId,
		purchaseOrderIds,
	});

	const verificationMutation = usePrintDisbursementSubmitVerificationMutation();
	const submitDocumentMutation = usePrintDisbursementSubmitDocumentMutation();
	const downloadDocumentMutation = useDownloadDocumentMutation();
	const printPurchaseOrderMutation = usePrintPurchaseOrder();

	const mainForm = useForm<
		Omit<PrintDisbursementSubmitDocumentPayload, 'documentSigningPicZipCode'> &
			Omit<PrintDisbursementSubmitVerificationPayload, 'covenants'> & {
				covenants: CovenantData[];
				documentSigningPicZipCode: AddressMasterData;
			}
	>();

	const [view, setView] = useState<
		| {
				type: 'main';
		  }
		| {
				type: 'view';
				data?: MPPDetail['transactionDetailSummary'][number];
		  }
	>({
		type: 'main',
	});

	const selectAddress = useSelectAddress();

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const {
		fcasList,
		fcdvsList,
		fcasName,
		fcdvsName,
		fcdvsNotes,
		documentSigningPicName,
		documentSigningPicIdCardNo,
		documentSigningPicPosition,
		documentSigningPicZipCode,
		documentSigningPicAddress,
		collectionPicName,
		collectionPicPhoneNo,
		collectionPicPosition,
		documentTemplates = [],
		approvalActions = [],
		approvalHistory,
		mppDetails,
	} = data?.data ?? {};

	const {
		customerName,
		customerId,
		bmppGroupName,
		bmppGroupId,
		industryClass,
		portfolioGuideline,
		ultimateOwner,
		mklNo,
		spfId,
		spfNo,
		napResolutionId,
		covenants = [],
		facilities,
		agingDate,
		transactionDetailSummary,
		facilityNotification,
		financingPurpose,
		prospectId,
		customerFacility,
		customerFacilitySummary,
		customerAffiliatesFacility,
		customerAffiliateFacilitySummary,
		obligorDetail,
		napPlafonTypeId,
		resolutionFile,
		backgroundRealisation,
		financingLimitTypeOptions = [],
		plafonTypeOptions = [],
		wayOfPaymentOptions = [],
		orderSource,
		slikInquiryId,
	} = mppDetails ?? {};

	const isReadOnly = groupName === 'FCAS';

	const renderMainForm = () => {
		const { form, fieldName, value } = mainForm;

		const totalPlafondApproved = customerFacilitySummary?.totalPlafon ?? 0;
		const totalRemainingPlafon =
			customerFacilitySummary?.totalRemainingPlafon ?? 0;

		return (
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				hidden={view.type !== 'main'}
				disabled={viewOnly}
				labelCol={{ span: 10 }}
				wrapperCol={{ span: 14 }}
				onFinish={async (values) => {
					if (isFormSubmitting) return;

					setIsFormSubmitting(true);
					try {
						switch (groupName) {
						case 'FCOH': {
							const isConfirmed = await modal.confirm({
								title: 'Apakah data yang Anda inputkan sudah sesuai?',
								cancelText: 'TIDAK',
								okText: 'YA',
							});

							if (!isConfirmed) return;
							const payload = {
								fcasId: values.fcasId,
								fcdvsId: values.fcdvsId,
								notes: values.notes ?? '',
								action: values.action,
								purchaseOrderIds: values.purchaseOrderIds ?? [],
								covenants: values.covenants.map((covenant) => {
									return {
										id: covenant.id!,
										additionalInformation:
											covenant.additionalInformation ?? undefined,
										statusId: covenant.status ?? undefined,
									};
								}),
							};
							await verificationMutation.mutateAsync(payload);

							await modal.success({
								title: 'Sukses',
							});

							navigate('DisbursementListPage');
							break;
						}
						case 'FCDVS': {
							const isConfirmed = await modal.confirm({
								title: 'Apakah Anda yakin ingin cetak dokumen pencairan?',
								cancelText: 'TIDAK',
								okText: 'YA',
							});

							if (!isConfirmed) return;
							const res = await submitDocumentMutation.mutateAsync({
								mppId,
								action: values.action,
								collectionPicName: values.collectionPicName,
								collectionPicPhoneNo: values.collectionPicPhoneNo,
								collectionPicPosition: values.collectionPicPosition,
								documentSigningPicAddress: values.documentSigningPicAddress,
								documentSigningPicIdCardNo: values.documentSigningPicIdCardNo,
								documentSigningPicName: values.documentSigningPicName,
								documentSigningPicPosition: values.documentSigningPicPosition,
								documentSigningPicZipCode:
									values.documentSigningPicZipCode?.postalId,
								notes: values.notes,
								purchaseOrderIds:
									transactionDetailSummary
										?.map((d) => d.purchaseOrderId!)
										?.filter(Boolean) ?? [],
							});

							const disbursementNo =
								typeof res.data === 'string' ? res.data : '-';

							await modal.success({
								title: 'Dokumen Kesepakatan Pencairan Berhasil Dibuat!',
								subTitle: (
									<Fragment>
										<span>
											Berikut adalah nomor Dokumen Pencairan Debitur Anda:
										</span>
										<div style={{ fontSize: 32, color: 'black' }}>
											{disbursementNo}
										</div>
									</Fragment>
								),
								okText: 'PREVIEW DOCUMENT',
							});

							notification.destroy();
							notification.info({
								message: 'Loading documents...',
							});

							const documentIds = documentTemplates
								.map((doc) => doc.documents.map((a) => a.id))
								.flat();

							const downloadRes = await Promise.allSettled(
								documentIds.map((id) =>
									downloadDisbursementDocument({ id, mppId }),
								),
							);

							notification.destroy();

							const url = downloadRes
								.map((res) => {
									return res.status === 'fulfilled'
										? res.value?.data?.signedUrl ?? ''
										: '';
								})
								.filter(Boolean);

							// Create file data objects for the modal by matching document IDs with URLs
							const fileDataObjects = documentTemplates
								.map((doc, docIndex) =>
									doc.documents.map((document, docDocIndex) => {
										// Calculate the index in the flat documentIds array
										const flatIndex =
											documentTemplates
												.slice(0, docIndex)
												.reduce((acc, d) => acc + d.documents.length, 0) +
											docDocIndex;

										return {
											id: document.id,
											name: document.name,
											url: url[flatIndex] || '',
										};
									}),
								)
								.flat()
								.filter((file) => file.url);

							await openPreviewDocumentModal({
								fileData: fileDataObjects,
								allowDownload: false,
								downloadAllDocuments: true,
								buttonPosition: 'top',
							});
							navigate('DisbursementListPage');
							break;
						}
					}
					} finally {
						setIsFormSubmitting(false);
					}
				}}
			>
				<Collapse
					defaultActiveKey={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}
					items={[
						{
							label: 'Informasi Dokumen Covenant',
							children: (
								<Fragment>
									<Form.Item
										name={fieldName('covenants')}
										initialValue={
											covenants.map((covenant) => {
												return {
													...covenant,
												};
											}) ?? []
										}
										wrapperCol={{ span: 24 }}
										rules={[
											{
												validator: () => {
													// Only validate covenants for FCOH role
													if (groupName !== 'FCOH') {
														// Non-FCOH roles don't need covenant validation
														return Promise.resolve();
													}

													// Only validate covenants that have documents
													const covenantsWithDocuments = covenants.filter(
														(c) => c.covenantDocument?.id,
													);

													if (covenantsWithDocuments.length === 0) {
														// No covenants with documents, so no validation needed
														return Promise.resolve();
													}

													// Check if any covenant with document is missing required fields
													const hasIncompleteCovenants = value?.covenants?.some(
														(covenant, index) => {
															// Find the original covenant to check if it has a document
															const originalCovenant = covenants[index];
															if (!originalCovenant?.covenantDocument?.id) {
																// No document, no validation needed
																return false;
															}

															// Has document, must have status
															return !covenant.status;
														},
													);

													if (hasIncompleteCovenants) {
														return Promise.reject(
															'Covenant yang memiliki dokumen wajib diisi status dan informasi tambahan',
														);
													}

													return Promise.resolve();
												},
											},
										]}
									>
										<Table
											scroll={{ x: 'max-content' }}
											bordered
											pagination={false}
											dataSource={
												value?.covenants?.map((record, index) => {
													return {
														...record,
														key: index + 1,
													};
												}) ?? []
											}
											columns={[
												{
													title: 'No',
													fixed: 'left',
													align: 'center',
													render: (_, __, index) => index + 1,
												},
												{
													fixed: 'left',
													title: 'Edit',
													align: 'center',
													render: (_, record, index) => {
														// Only show edit button for FCOH group and when covenant has document
														const originalCovenant = covenants[index];
														if (
															groupName !== 'FCOH' ||
															!originalCovenant?.covenantDocument?.id
														) {
															return (
																<EditOutlined style={{ color: '#bababa' }} />
															);
														}

														return (
															<a
																onClick={async (e) => {
																	e.preventDefault();
																	const updatedCovenant =
																		await covenantModal.open({
																			...record,
																			id: record.id,
																			additionalInformation:
																				record.additionalInformation,
																			status: record.status,
																		});

																	if (!updatedCovenant) return;
																	form.setFieldValue(
																		fieldName('covenants'),
																		(value?.covenants ?? []).map(
																			(record, recordIdx) =>
																				index === recordIdx
																					? {
																							...record,
																							...updatedCovenant,
																						}
																					: record,
																		),
																	);
																}}
															>
																<EditOutlined />
															</a>
														);
													},
												},
												{
													title: 'Covenant',
													render: (_, record) => record.covenant,
												},
												{
													title: 'Kategori',
													render: (_, record) => record.category?.value ?? '-',
												},
												{
													title: 'Priority',
													render: (_, record) => record.priority?.value ?? '-',
												},
												{
													title: 'Repeat',
													render: (_, record) =>
														record.repeatFrequency?.value ?? '-',
												},
												{
													title: 'Jenis Monitoring',
													render: (_, record) =>
														record.monitoringType?.value ?? '-',
												},
												{
													title: 'Analisa Lebih Lanjut',
													render: (_, record) =>
														record.furtherAnalysis?.value ?? '-',
												},
												{
													title: 'Tanggal Upload Dokumen',
													render: (_, record) =>
														record.uploadDocumentDate
															? dayjs(record.uploadDocumentDate).format(
																	'DD/MM/YYYY',
																)
															: '-',
												},
												{
													title: 'View Dokumen',
													fixed: 'right',
													align: 'center',
													render: (_, record) => {
														if (!record.covenantDocument?.id) {
															return (
																<FileSearchOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}
														return (
															<a
																onClick={(e) => {
																	e.preventDefault();
																	if (!record.covenantDocument?.id) return;
																	openPreviewModal(record.covenantDocument);
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'Status',
													render: (_, record) =>
														covenantStatusOptions.find(
															(option) => String(option.id) === record.status,
														)?.value ?? '-',
												},
												{
													title: 'Due Date',
													render: (_, record) =>
														record.dueDate
															? dayjs(record.dueDate).format('DD/MM/YYYY')
															: '-',
												},
												{
													title: 'OD',
													render: (_, __, recordIndex) => {
														const od = covenants?.find(
															(_, index) => index === recordIndex,
														)?.overdueDays;

														return typeof od === 'number' ? `${od} Hari` : '-';
													},
												},
												{
													title: 'Keterangan',
													render: (_, record) => record.notes ?? '-',
												},
												{
													title: 'Keterangan FCOH',
													render: (_, record) =>
														record.additionalInformation ?? '-',
												},
											]}
										/>
									</Form.Item>

									{/* Warning messages for covenants with documents but missing required fields */}
									{groupName === 'FCOH' &&
										(() => {
											const incompleteCovenants =
												value?.covenants?.filter((covenant, index) => {
													const originalCovenant = covenants[index];
													return (
														originalCovenant?.covenantDocument?.id &&
														!covenant.status
													);
												}) ?? [];

											if (incompleteCovenants.length > 0) {
												return (
													<Alert
														type="warning"
														showIcon
														style={{
															marginTop: 16,
														}}
														className="custom-alert"
														message={
															<span style={{ fontWeight: 'bold' }}>
																{incompleteCovenants.length} covenant perlu
																dilengkapi
															</span>
														}
														description={
															<span
																style={{
																	fontSize: '12px',
																	fontWeight: 'normal',
																}}
															>
																Covenant yang memiliki dokumen wajib diisi
																status dan informasi tambahan sebelum dapat
																melanjutkan proses pencairan.
															</span>
														}
													/>
												);
											}
											return null;
										})()}
								</Fragment>
							),
						},
						{
							label: 'Informasi Approval Pemberitahuan Fasilitas',
							children: (
								<Fragment>
									{facilityNotification?.map((record, index) => {
										const isMultiguna = record.financingTypes.some(
											(d) => d.value === 'Pembiayaan Multiguna',
										);

										return (
											<Fragment key={index}>
												<div style={{ marginBottom: 10 }}>
													<strong>Limit Plafon {index + 1}</strong>
												</div>
												<Table
													bordered
													pagination={false}
													dataSource={
														[
															{
																subject: 'Jenis Pembiayaan',
																approval:
																	record.financingTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Jenis Fasilitas',
																approval:
																	record.facilityTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Limit Pembiayaan',
																approval: record.approvedLimit
																	? formatCurrency(record.approvedLimit)
																	: '-',
															},
															{
																subject: 'Sifat Limit Pembiayaan',
																approval:
																	financingLimitTypeOptions?.find(
																		(option) =>
																			option.id === record.financingLimitTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Jenis Plafon',
																approval:
																	plafonTypeOptions?.find(
																		(option) =>
																			option.id === record.plafonTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Way Of Payment',
																approval:
																	wayOfPaymentOptions?.find(
																		(option) =>
																			option.id === record.wayOfPaymentId,
																	)?.value ?? '-',
															},
															{
																subject: 'Masa Berlaku Fasilitas',
																approval: record.facilityValidityPeriod
																	? dayjs(record.facilityValidityPeriod).format(
																			'DD/MM/YYYY',
																		)
																	: '-',
															},
															...(isMultiguna
																? [
																		{
																			subject: 'Mekanisme Pencairan',
																			approval:
																				record.disbursementMechanism ?? '-',
																		},
																		{
																			subject: 'Kondisi Objek Pembiayaan',
																			approval:
																				record.financingObjectCondition ?? '-',
																		},
																		{
																			subject: 'Penilaian Objek Pembiayaan',
																			approval:
																				record.financingObjectEvaluation ?? '-',
																		},
																	]
																: []),
														] as Record<'subject' | 'approval', string>[]
													}
													columns={[
														{
															title: 'Perihal',
															width: '50%',
															render: (_, record) => record.subject,
														},
														{
															title: 'Keterangan Approval',
															width: '50%',
															render: (_, record) => <b>{record.approval}</b>,
														},
													]}
												/>
												<Divider />
											</Fragment>
										);
									})}
									<Typography.Title level={3}>
										Detail Fasilitas
									</Typography.Title>
									<Table
										dataSource={facilities ?? []}
										columns={[
											{
												title: 'No',
												align: 'center',
												render: (_, __, index: number) => index + 1,
											},
											{
												title: 'Jenis Asset',
												render: (_, record) => record.assetType?.value,
											},
											{
												title: 'Kondisi Obyek Pembiayaan',
												render: (_, record) =>
													record.financingObjectCondition?.value,
											},
											{
												title: 'Sifat Limit Pembiayaan',
												render: (_, record) =>
													record.financingLimitType?.value ?? '-',
											},
											{
												title: 'Jenis Plafon',
												render: (_, record) => record.plafonType?.value,
											},
											{
												title: 'Tenor Pembiayaan',
												render: (_, record) => record.tenor?.value,
											},
											{
												title: 'Indikasi Suku Bunga (Eff)',
												render: (_, record) =>
													record.interestRateIndicationPercent
														? `${formatCurrencyWithDot(record.interestRateIndicationPercent)}%`
														: '-',
											},
											{
												title: 'Down Payment',
												render: (_, record) =>
													record.downPaymentPercent
														? `${formatCurrencyWithDot(record.downPaymentPercent)}%`
														: '-',
											},
											{
												title: 'Security Deposit',
												render: (_, record) =>
													record.securityDeposit
														? formatCurrency(record.securityDeposit)
														: '-',
											},
											{
												title: 'Biaya Administrasi',
												render: (_, record) =>
													record.administrativeFee
														? formatCurrency(record.administrativeFee)
														: '-',
											},
											{
												title: 'Provisi',
												render: (_, record) =>
													record.provisionFee
														? formatCurrencyWithDot(record.provisionFee) + '%'
														: '-',
											},
											{
												title: 'Biaya Layanan',
												render: (_, record) =>
													record.serviceFee
														? `${formatCurrencyWithDot(record.serviceFee)}%`
														: '-',
											},
											{
												title: 'Asuransi',
												render: (_, record) => record.insurance?.value ?? '-',
											},
											{
												title: 'Diskon Asuransi',
												align: 'center',
												render: (_, record) =>
													record.insuranceDiscountPercentage
														? `${formatCurrencyWithDot(record.insuranceDiscountPercentage)}%`
														: '-',
											},
											{
												title: 'Fidusia',
												render: (_, record) =>
													record.fiduciaryFee
														? formatCurrency(record.fiduciaryFee)
														: '-',
											},
										]}
										bordered
										pagination={false}
										scroll={{ x: 'max-content' }}
									/>
								</Fragment>
							),
						},
						{
							label: 'Memo Pencairan Pembiayaan',
							children: (
								<Fragment>
									<Typography.Title level={3}>
										Latar Belakang Realisasi
									</Typography.Title>
									<Form.Item wrapperCol={{ span: 24 }}>
										<Input.TextArea
											rows={4}
											readOnly={true}
											value={backgroundRealisation}
										/>
									</Form.Item>
									<Divider />
									<div style={{ marginBottom: '20px', fontWeight: 'bold' }}>
										Informasi Realisasi
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												background: 'white',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: '20px',
											}}
										>
											<thead>
												<tr
													style={{ backgroundColor: '#2D4C82', color: 'white' }}
												>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '160px',
														}}
													>
														Nama Customer
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '80px',
														}}
													>
														Fasilitas
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														No. MPP
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														NTF
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														OS Pokok Hutang
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														Angsuran/Bulan
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '100px',
														}}
													>
														Jumlah Unit
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														Detail PO Pending
													</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													// Get unique MPP numbers for existing facilities
													const existingFacilities =
														customerFacility?.existingFacilities || [];
													const uniqueExistingMppNos = Array.from(
														new Set(
															existingFacilities.map(
																(facility) => facility.mppNo || '',
															),
														),
													);

													// Sort pending facilities by MPP No first to ensure proper grouping
													const unsortedPendingFacilities =
														customerFacility?.pendingFacilities || [];
													const pendingFacilities = [
														...unsortedPendingFacilities,
													].sort((a, b) => {
														const aMppNo = a.mppNo || '';
														const bMppNo = b.mppNo || '';
														return aMppNo.localeCompare(bMppNo);
													});
													const uniquePendingMppNos = Array.from(
														new Set(
															pendingFacilities.map(
																(facility) => facility.mppNo || '',
															),
														),
													);

													// Always show at least one row for existing and pending
													const hasExistingRows =
														uniqueExistingMppNos.length > 0;
													const hasPendingRows = uniquePendingMppNos.length > 0;

													// If no existing facilities, add a placeholder MPP number
													if (!hasExistingRows) {
														uniqueExistingMppNos.push('');
													}

													// If no pending facilities, add a placeholder MPP number
													if (!hasPendingRows) {
														uniquePendingMppNos.push('');
													}

													return (
														<>
															{/* Existing Facilities - one row per facility */}
															{(() => {
																// Group facilities by MPP No and calculate rowspan
																const mppNoGroups = existingFacilities.reduce(
																	(groups, facility) => {
																		const mppNo = facility.mppNo || '';
																		if (!groups[mppNo]) {
																			groups[mppNo] = [];
																		}
																		groups[mppNo].push(facility);
																		return groups;
																	},
																	{} as Record<
																		string,
																		typeof existingFacilities
																	>,
																);

																return existingFacilities.map(
																	(facility, index) => {
																		const facilityNtf = facility.ntf ?? 0;
																		const facilityOutstanding =
																			facility.outstanding ?? 0;
																		const facilityInstallment =
																			facility.installment ?? 0;
																		const facilityUnitCount =
																			facility.unitCount ?? 0;
																		const facilityPoDetails =
																			facility.poNo || '';

																		const mppNo = facility.mppNo || '';
																		const facilitiesInGroup =
																			mppNoGroups[mppNo];
																		const facilityIndex =
																			facilitiesInGroup.indexOf(facility);
																		const isFirstInGroup = facilityIndex === 0;
																		const rowspan = facilitiesInGroup.length;
																		const showMppNoCell = isFirstInGroup;

																		return (
																			<tr key={`existing-${index}`}>
																				{/* Show customer name only for the first facility */}
																				{index === 0 && (
																					<>
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																								width: '160px',
																							}}
																						>
																							{customerName}
																						</td>
																						<td
																							rowSpan={
																								existingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								width: '80px',
																							}}
																						>
																							Eksisting
																						</td>
																					</>
																				)}
																				{showMppNoCell && (
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							color: '#1890ff',
																							width: '190px',
																						}}
																						rowSpan={rowspan}
																					>
																						{mppNo || '-'}
																					</td>
																				)}
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '130px',
																					}}
																				>
																					{formatCurrency(facilityNtf)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '130px',
																					}}
																				>
																					{formatCurrency(facilityOutstanding)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '130px',
																					}}
																				>
																					{formatCurrency(facilityInstallment)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '100px',
																					}}
																				>
																					{facilityUnitCount}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						width: '190px',
																					}}
																				>
																					{facilityPoDetails}
																				</td>
																			</tr>
																		);
																	},
																);
															})()}
															{/* Pending Facilities - one row per facility */}
															{(() => {
																// Group facilities by MPP No and calculate rowspan
																const mppNoGroups = pendingFacilities.reduce(
																	(groups, facility) => {
																		const mppNo = facility.mppNo || '';
																		if (!groups[mppNo]) {
																			groups[mppNo] = [];
																		}
																		groups[mppNo].push(facility);
																		return groups;
																	},
																	{} as Record<
																		string,
																		typeof pendingFacilities
																	>,
																);

																return pendingFacilities.map(
																	(facility, index) => {
																		const facilityNtf = facility.ntf ?? 0;
																		const facilityOutstanding =
																			facility.outstanding ?? 0;
																		const facilityInstallment =
																			facility.installment ?? 0;
																		const facilityUnitCount =
																			facility.unitCount ?? 0;
																		const facilityPoDetails =
																			facility.poNo || '';

																		const mppNo = facility.mppNo || '';
																		const facilitiesInGroup =
																			mppNoGroups[mppNo];
																		const facilityIndex =
																			facilitiesInGroup.indexOf(facility);
																		const isFirstInGroup = facilityIndex === 0;
																		const rowspan = facilitiesInGroup.length;
																		const showMppNoCell = isFirstInGroup;

																		return (
																			<tr key={`pending-${index}`}>
																				{/* Show customer name only if there are no existing facilities */}
																				{existingFacilities.length === 0 &&
																					index === 0 && (
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																								width: '160px',
																							}}
																						>
																							{customerName}
																						</td>
																					)}
																				{/* Show "Pending" label only for the first facility in each MPP group */}
																				{isFirstInGroup && (
																					<td
																						rowSpan={rowspan}
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																						}}
																					>
																						Pending
																					</td>
																				)}
																				{showMppNoCell && (
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							color: '#1890ff',
																							width: '190px',
																						}}
																						rowSpan={rowspan}
																					>
																						{mppNo || '-'}
																					</td>
																				)}
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '130px',
																					}}
																				>
																					{formatCurrency(facilityNtf)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '130px',
																					}}
																				>
																					{formatCurrency(facilityOutstanding)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '130px',
																					}}
																				>
																					{formatCurrency(facilityInstallment)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																						width: '100px',
																					}}
																				>
																					{facilityUnitCount}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						width: '190px',
																					}}
																				>
																					{facilityPoDetails}
																				</td>
																			</tr>
																		);
																	},
																);
															})()}
														</>
													);
												})()}
												<tr>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														Pengajuan
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{formatCurrency(
															customerFacility?.requestedFacilities[0]?.ntf ||
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{formatCurrency(
															customerFacility?.requestedFacilities[0]
																?.outstanding || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{formatCurrency(
															customerFacility?.requestedFacilities[0]
																?.installment || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '100px',
														}}
													>
														{customerFacility?.requestedFacilities[0]
															?.unitCount || ''}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															width: '190px',
														}}
													/>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.totalNtf || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.totalOutstanding || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.installmentAmount || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '100px',
														}}
													>
														{customerFacilitySummary?.totalUnitCount || ''}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															width: '190px',
														}}
													></td>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total Pengajuan Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{customerFacilitySummary?.totalCustomerApplication
															? formatCurrency(
																	customerFacilitySummary?.totalCustomerApplication,
																)
															: ''}
													</td>
													<td
														colSpan={4}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													/>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Plafond Customer Yang Disetujui
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{totalPlafondApproved
															? formatCurrency(totalPlafondApproved)
															: ''}
													</td>
													<td
														colSpan={4}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													/>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Sisa Plafond Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													>
														{totalRemainingPlafon
															? formatCurrency(totalRemainingPlafon)
															: ''}
													</td>
													<td
														colSpan={4}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															width: '130px',
														}}
													/>
												</tr>
											</tbody>
										</table>
									</div>

									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												background: 'white',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: '20px',
											}}
										>
											<thead>
												<tr
													style={{ backgroundColor: '#2D4C82', color: 'white' }}
												>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '160px',
														}}
													>
														Nama Customer
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '80px',
														}}
													>
														Fasilitas
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														No. MPP
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														NTF
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														OS Pokok Hutang
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														Angsuran/Bulan
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '100px',
														}}
													>
														Jumlah Unit
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														Detail PO Pending
													</th>
												</tr>
											</thead>
											<tbody>
												{customerAffiliatesFacility?.map(
													(
														{
															customerAffiliateName,
															customerAffiliateFacilities,
														},
														affiliateIndex,
													) => {
														// Collect existing facilities with metadata
														const existingFacilities =
															customerAffiliateFacilities.flatMap(
																(facilityCollection, collectionIndex) => {
																	return (
																		facilityCollection.existingFacilities || []
																	).map((facility, facilityIndex) => ({
																		...facility,
																		type: 'existing' as const,
																		collectionIndex,
																		uniqueId: `${affiliateIndex}-${collectionIndex}-existing-${facilityIndex}`,
																	}));
																},
															);

														// Collect pending facilities with metadata
														const unsortedPendingFacilities =
															customerAffiliateFacilities.flatMap(
																(facilityCollection, collectionIndex) => {
																	return (
																		facilityCollection.pendingFacilities || []
																	).map((facility, facilityIndex) => ({
																		...facility,
																		type: 'pending' as const,
																		collectionIndex,
																		uniqueId: `${affiliateIndex}-${collectionIndex}-pending-${facilityIndex}`,
																	}));
																},
															);

														// Sort pending facilities by MPP No first to ensure proper grouping
														const pendingFacilities = [
															...unsortedPendingFacilities,
														].sort((a, b) => {
															const aMppNo = a.mppNo || '';
															const bMppNo = b.mppNo || '';
															return aMppNo.localeCompare(bMppNo);
														});

														// Group pending facilities by MPP No to ensure proper ordering
														const pendingByMppNo = pendingFacilities.reduce(
															(groups, facility) => {
																const mppNo = facility.mppNo || '';
																if (!groups[mppNo]) {
																	groups[mppNo] = [];
																}
																groups[mppNo].push(facility);
																return groups;
															},
															{} as Record<string, typeof pendingFacilities>,
														);

														// Sort pending facilities: group by MPP No
														const sortedPendingMppNos =
															Object.keys(pendingByMppNo).sort();

														// Calculate totals
														const totalPendingFacilities =
															pendingFacilities.length;
														const totalExistingFacilities =
															existingFacilities.length;
														const hasExistingFacilities =
															totalExistingFacilities > 0;

														return (
															<>
																{/* Render existing facilities first */}
																{existingFacilities.map((facility, index) => {
																	const facilityNtf = facility.ntf ?? 0;
																	const facilityOutstanding =
																		facility.outstanding ?? 0;
																	const facilityInstallment =
																		facility.installment ?? 0;
																	const facilityUnitCount =
																		facility.unitCount ?? 0;
																	const facilityPoDetails = facility.poNo || '';

																	const isFirstFacility = index === 0;
																	const mppNo = facility.mppNo || '';

																	return (
																		<tr key={facility.uniqueId}>
																			{/* Show customer name only for the first facility */}
																			{isFirstFacility && (
																				<td
																					rowSpan={
																						existingFacilities.length +
																						pendingFacilities.length
																					}
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						verticalAlign: 'top',
																					}}
																				>
																					{customerAffiliateName}
																				</td>
																			)}
																			{/* Show "Eksisting" label only for the first existing facility */}
																			{isFirstFacility && (
																				<td
																					rowSpan={existingFacilities.length}
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																					}}
																				>
																					Eksisting
																				</td>
																			)}
																			{/* Show MPP No */}
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					color: '#1890ff',
																				}}
																			>
																				{mppNo || '-'}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityNtf)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityOutstanding)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityInstallment)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{facilityUnitCount ?? 0}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					whiteSpace: 'nowrap',
																				}}
																			>
																				{facilityPoDetails}
																			</td>
																		</tr>
																	);
																})}

																{/* Render pending facilities grouped by MPP No */}
																{sortedPendingMppNos.map((mppNo, mppIndex) => {
																	const facilitiesInGroup =
																		pendingByMppNo[mppNo];

																	return facilitiesInGroup.map(
																		(facility, index) => {
																			const facilityNtf = facility.ntf ?? 0;
																			const facilityOutstanding =
																				facility.outstanding ?? 0;
																			const facilityInstallment =
																				facility.installment ?? 0;
																			const facilityUnitCount =
																				facility.unitCount ?? 0;
																			const facilityPoDetails =
																				facility.poNo || '';

																			const isFirstInGroup = index === 0;
																			const isFirstPendingOverall =
																				mppIndex === 0 && index === 0;
																			const showCustomerName =
																				isFirstPendingOverall &&
																				!hasExistingFacilities;

																			return (
																				<tr key={facility.uniqueId}>
																					{/* Show customer name only if no existing facilities and this is the first pending facility */}
																					{showCustomerName && (
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																							}}
																						>
																							{customerAffiliateName}
																						</td>
																					)}
																					{/* Show "Pending" label only for the first pending facility overall */}
																					{isFirstPendingOverall && (
																						<td
																							rowSpan={totalPendingFacilities}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																							}}
																						>
																							Pending
																						</td>
																					)}
																					{/* Show MPP No only for the first facility in this MPP group */}
																					{isFirstInGroup && (
																						<td
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								color: '#1890ff',
																							}}
																							rowSpan={facilitiesInGroup.length}
																						>
																							{facility.mppNo || '-'}
																						</td>
																					)}
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{formatCurrency(facilityNtf)}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{formatCurrency(
																							facilityOutstanding,
																						)}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{formatCurrency(
																							facilityInstallment,
																						)}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{facilityUnitCount ?? 0}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							whiteSpace: 'nowrap',
																						}}
																					>
																						{facilityPoDetails}
																					</td>
																				</tr>
																			);
																		},
																	);
																})}
															</>
														);
													},
												)}
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total Afiliasi Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalNtf || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalOutstanding ||
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.installmentAmount ||
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{customerAffiliateFacilitySummary?.totalUnitCount ||
															''}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													/>
												</tr>
											</tbody>
										</table>
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: 20,
											}}
										>
											<tbody>
												<tr style={{ backgroundColor: '#f0f0f0' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														GRAND TOTAL PENGAJUAN (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(
															obligorDetail?.grandTotalApplication,
														)}
													</td>
												</tr>
												<tr style={{ backgroundColor: '#f0f0f0' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														PLAFOND YANG DISETUJUI (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(obligorDetail?.grandTotalPlafon)}
													</td>
												</tr>
												<tr style={{ backgroundColor: '#fffbe6' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														SISA PLAFOND (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(
															obligorDetail?.grandTotalRemainingPlafon,
														)}
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<Form.Item
										label="Aging Date"
										labelCol={{ span: 5 }}
										wrapperCol={{ span: 19 }}
									>
										<DatePicker
											style={{ width: 200 }}
											value={dayjs(agingDate)}
											disabled
										/>
									</Form.Item>

									<Divider />

									<div style={{ marginBottom: 20 }}>
										<strong>
											{groupName === 'FCDVS'
												? 'Ringkasan Rincian Detail Transaksi'
												: 'Rincian Detail Transaksi'}
										</strong>
									</div>

									<Form.Item
										name={fieldName('purchaseOrderIds')}
										initialValue={[]}
										rules={[
											{
												required: true,
												validator: (_, value) => {
													if (
														groupName === 'FCOH' &&
														(!value || value.length === 0)
													) {
														return Promise.reject(
															new Error(
																'Pilih minimal satu Purchase Order untuk melanjutkan',
															),
														);
													}
													return Promise.resolve();
												},
											},
										]}
										wrapperCol={{ span: 24 }}
									>
										<Table
											bordered
											pagination={false}
											scroll={{ x: 'max-content' }}
											dataSource={
												transactionDetailSummary?.map((record, key) => {
													return {
														...record,
														key,
													};
												}) ?? []
											}
											columns={[
												{
													title: 'No',
													align: 'center',
													fixed: 'left',
													render: (_, __, index) => index + 1,
												},
												{
													hidden: groupName === 'FCDVS' || groupName === 'FCAS',
													title: 'Edit Simulasi',
													align: 'center',
													render: (_, record) => {
														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();

																	navigate('FRMSimulationPage', {
																		simulationId: record.creditSimulationId,
																		onBack: () => {
																			navigate('DisbursementPage', {
																				mppId: mppId,
																				purchaseOrderIds: purchaseOrderIds,
																				onBack: onBack,
																			});
																		},
																	});
																}}
															>
																<EditOutlined />
															</a>
														);
													},
												},
												{
													title: 'View Detail',
													align: 'center',
													fixed: 'left',
													render: (_, record) => {
														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	setView({
																		type: 'view',
																		data: record,
																	});
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'No. Simulasi',
													render: (_, record) =>
														record.creditSimulationNo ? (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	navigate('SimulationPage', {
																		simulationId: record.creditSimulationId,
																		showResult: true,
																		viewOnly: true,
																		onBack: () => {
																			navigate('DisbursementPage', {
																				mppId,
																				purchaseOrderIds,
																				financingProductId,
																				onBack,
																			});
																		},
																	});
																}}
																style={{ cursor: 'pointer' }}
															>
																{record.creditSimulationNo}
															</a>
														) : (
															'-'
														),
												},
												{
													title: 'No. PO',
													render: (_, record) =>
														record.purchaseOrderNo && record.purchaseOrderId ? (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const documents =
																		await printPurchaseOrderMutation.mutateAsync(
																			{
																				purchaseOrderIds: [
																					record.purchaseOrderId!,
																				],
																			},
																		);

																	openDocumentModal(documents);
																}}
															>
																{record.purchaseOrderNo}
															</a>
														) : (
															'-'
														),
												},
												{
													title: 'Skema Pembiayaan',
													render: (_, record) =>
														record.financingProduct?.value ?? '-',
												},
												{
													title: 'Status PO',
													render: (_, record) =>
														record.purchaseOrderStatus ?? '-',
												},
												{
													title: 'Jenis Barang',
													render: (_, record) => record.assetType ?? '-',
												},
												{
													title: 'Product ID',
													render: (_, record) => record.productId ?? '-',
												},
												{
													title: 'Merk Kendaraan',
													render: (_, record) => record.merk ?? '-',
												},
												{
													title: 'Jumlah Unit',
													render: (_, record) => record.unitCount ?? '-',
												},
												{
													title: 'Total NTF',
													render: (_, record) =>
														record.totalNtf
															? formatCurrency(record.totalNtf)
															: '-',
												},
												{
													title: 'Total Angsuran/bulan',
													render: (_, record) =>
														record.totalInstallment
															? formatCurrency(record.totalInstallment)
															: '-',
												},
												{
													title: 'Total Down Payment',
													render: (_, record) =>
														record.totalDownPayment
															? formatCurrency(record.totalDownPayment)
															: '-',
												},
												{
													title: 'Tipe Asuransi',
													render: (_, record) => record.insuranceType ?? '-',
												},
												{
													hidden: groupName === 'FCDVS' || groupName === 'FCAS',
													title: 'Verifikasi',
													align: 'center',
													fixed: 'right',
													render: (_, record) => {
														// const isChecked = record.purchaseOrderId
														// 	? (value?.purchaseOrderIds ?? []).includes(
														// 			record.purchaseOrderId,
														// 		) || record.isVerified
														// 	: false;
														if (!record.allowFcohCheck) {
															return (
																<Checkbox
																	checked
																	disabled
																/>
															);
														}
														return (
															<Checkbox
																onChange={(e) => {
																	const { checked } = e.target;
																	const ids = value?.purchaseOrderIds ?? [];

																	const purchaseOrderIds = checked
																		? record.purchaseOrderId
																			? [...ids, record.purchaseOrderId]
																			: ids
																		: ids.filter(
																				(id) => id !== record.purchaseOrderId,
																			);

																	form.setFieldValue(
																		fieldName('purchaseOrderIds'),
																		purchaseOrderIds,
																	);
																}}
															/>
														);
													},
												},
												{
													hidden: groupName === 'FCOH',
													title: 'Dokumen SPK',
													fixed: 'right',
													align: 'center',
													colSpan: 2,
													render: (_, record) => {
														if (!record.spkFile) {
															return (
																<DownloadOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();

																	const link = document.createElement('a');
																	link.href = record.spkFile.url;

																	document.body.appendChild(link);

																	link.click();
																	document.body.removeChild(link);
																}}
															>
																<DownloadOutlined />
															</a>
														);
													},
												},
												{
													hidden: groupName === 'FCOH',
													colSpan: 0,
													fixed: 'right',
													align: 'center',
													render: (_, record) => {
														if (!record.spkFile) {
															return (
																<FileSearchOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	openPreviewModal(record.spkFile);
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													hidden: groupName === 'FCOH',
													title: 'Verifikasi FCOH',
													align: 'center',
													fixed: 'right',
													render: (_, record) => {
														return (
															<Checkbox
																checked={record.isVerified}
																disabled
															/>
														);
													},
												},
											]}
										/>
									</Form.Item>
								</Fragment>
							),
						},
						...(groupName === 'FCOH'
							? [
									{
										label: 'Verifikasi Dokumen Pencairan',
										children: (
											<Fragment>
												<FleetTitle style={{ marginBottom: 20 }}>
													Assign Credit Data Entry & Credit Document Reviewer
												</FleetTitle>
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item
															name={fieldName('fcasId')}
															label="Pilih Fleet Credit Administration Staff (FCAS)"
															labelCol={{ span: 12 }}
															wrapperCol={{ span: 12 }}
															rules={[rule.required()]}
														>
															<Select
																style={{ width: '100%' }}
																options={fcasList?.map((option) => {
																	return {
																		label: option.value,
																		value: option.id,
																	};
																})}
															/>
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item
															name={fieldName('fcdvsId')}
															label="Pilih Fleet Credit Document Verification Staff (FCDVS)"
															labelCol={{ span: 12 }}
															wrapperCol={{ span: 12 }}
															rules={[rule.required()]}
														>
															<Select
																style={{ width: '100%' }}
																options={fcdvsList?.map((option) => {
																	return {
																		label: option.value,
																		value: option.id,
																	};
																})}
															/>
														</Form.Item>
													</Col>
												</Row>
											</Fragment>
										),
									},
								]
							: []),
						...(groupName === 'FCDVS' || groupName === 'FCAS'
							? [
									{
										label: 'Informasi Person In Charge',
										children: (
											<Fragment>
												<FleetTitle style={{ marginBottom: 20 }}>
													Informasi PIC Tanda Tangan Dokumen
												</FleetTitle>
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item
															name={fieldName('documentSigningPicName')}
															label="Nama"
															rules={isReadOnly ? [] : [rule.required()]}
															initialValue={documentSigningPicName}
														>
															<Input disabled={isReadOnly} />
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item
															name={fieldName('documentSigningPicIdCardNo')}
															label="No. KTP"
															rules={
																isReadOnly
																	? []
																	: [
																			rule.required(),
																			rule.strNumber(),
																			...rules.ktp,
																		]
															}
															initialValue={documentSigningPicIdCardNo}
														>
															<Input
																maxLength={16}
																minLength={16}
																disabled={isReadOnly}
															/>
														</Form.Item>
													</Col>
												</Row>
												<Divider />
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item
															name={fieldName('documentSigningPicPosition')}
															label="Jabatan"
															rules={isReadOnly ? [] : [rule.required()]}
															initialValue={documentSigningPicPosition}
														>
															<Input disabled={isReadOnly} />
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item
															name={fieldName('documentSigningPicZipCode')}
															label="Kode Pos"
															rules={isReadOnly ? [] : [rule.required()]}
															initialValue={documentSigningPicZipCode}
															getValueFromEvent={(_, option) => option?.data}
															getValueProps={(value) => {
																if (!value) return {};
																return {
																	value: {
																		value: value.postalId,
																		label: value.postalCode,
																	},
																};
															}}
														>
															<Select
																{...selectAddress}
																disabled={isReadOnly}
																onSelect={(value, option) => {
																	selectAddress.onSelect!(value, option);
																	form.setFieldValue(
																		fieldName('documentSigningPicZipCode'),
																		option.data,
																	);
																}}
																onClear={() => {
																	form.setFieldValue(
																		fieldName('documentSigningPicZipCode'),
																		null,
																	);
																}}
															/>
														</Form.Item>
													</Col>
												</Row>
												<Divider />
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item label="Kelurahan">
															<Input
																disabled
																value={
																	value?.documentSigningPicZipCode
																		?.locationName ?? '-'
																}
															/>
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item label="Kecamatan">
															<Input
																disabled
																value={
																	value?.documentSigningPicZipCode
																		?.districtName ?? '-'
																}
															/>
														</Form.Item>
													</Col>
												</Row>
												<Divider />
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item label="Kota">
															<Input
																disabled
																value={
																	value?.documentSigningPicZipCode?.cityName ??
																	'-'
																}
															/>
														</Form.Item>
													</Col>
												</Row>
												<Divider />
												<Form.Item
													name={fieldName('documentSigningPicAddress')}
													rules={isReadOnly ? [] : [rule.required()]}
													label="Alamat"
													labelCol={{ span: 5 }}
													wrapperCol={{ span: 19 }}
													initialValue={documentSigningPicAddress}
												>
													<Input.TextArea
														rows={2}
														disabled={isReadOnly}
													/>
												</Form.Item>

												<FleetTitle style={{ marginBlock: 20 }}>
													Informasi PIC Penagihan
												</FleetTitle>
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item
															name={fieldName('collectionPicName')}
															label="Nama"
															rules={isReadOnly ? [] : [rule.required()]}
															initialValue={collectionPicName}
														>
															<Input disabled={isReadOnly} />
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item
															label="No. Telepon"
															name={fieldName('collectionPicPhoneNo')}
															rules={
																isReadOnly
																	? []
																	: [
																			rule.required(),
																			rule.strNumber(),
																			rule.phoneNumber(),
																		]
															}
															initialValue={collectionPicPhoneNo}
														>
															<Input disabled={isReadOnly} />
														</Form.Item>
													</Col>
												</Row>
												<Divider />
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item
															name={fieldName('collectionPicPosition')}
															label="Jabatan"
															rules={isReadOnly ? [] : [rule.required()]}
															initialValue={collectionPicPosition}
														>
															<Input disabled={isReadOnly} />
														</Form.Item>
													</Col>
												</Row>
											</Fragment>
										),
									},
									{
										label:
											'Informasi Dokumen Kesepakatan Realisasi Pencairan Pembiayaan',
										children: (
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: 20,
												}}
											>
												{/* Download All Button */}
												{documentTemplates.length > 0 && (
													<Button
														type="primary"
														icon={<DownloadOutlined />}
														style={{
															alignSelf: 'flex-start',
															marginBottom: 16,
														}}
														onClick={async () => {
															try {
																const documentIds = documentTemplates
																	.map((doc) => doc.documents.map((a) => a.id))
																	.flat()
																	.filter((id) => id !== undefined);

																const documentNames = documentTemplates
																	.map((doc) =>
																		doc.documents.map((a) => a.name),
																	)
																	.flat()
																	.filter((name) => name !== undefined);

																if (documentIds.length === 0) {
																	notification.error({
																		message:
																			'No documents available for download',
																	});
																	return;
																}

																notification.info({
																	message: 'Preparing download...',
																});

																await downloadDocumentsAsZip(
																	documentIds,
																	documentNames,
																	downloadDocumentMutation.mutateAsync,
																	mppId,
																	'Dokumen Kesepakatan Realisasi Pencairan Pembiayaan',
																);

																notification.destroy();
																notification.success({
																	message:
																		'All documents downloaded successfully',
																});
															} catch (error) {
																notification.destroy();
																notification.error({
																	message: 'Failed to download documents',
																	description:
																		error instanceof Error
																			? error.message
																			: 'Unknown error',
																});
															}
														}}
													>
														Download All Documents
													</Button>
												)}

												{documentTemplates.map((template) => (
													<Fragment>
														<FleetTitle>{template.documentTypeName}</FleetTitle>
														<Table
															bordered
															pagination={false}
															dataSource={template.documents}
															columns={[
																{
																	title: 'No',
																	align: 'center',
																	fixed: 'left',
																	width: 1,
																	render: (_, __, index) => index + 1,
																},
																{
																	title: 'Nama Dokumen',
																	render: (_, record) => record.name,
																},
																{
																	title: 'View Dokumen',
																	fixed: 'right',
																	align: 'center',
																	width: 130,
																	render: (_, record) => {
																		if (!record.id)
																			return (
																				<FileSearchOutlined
																					style={{ color: '#bababa' }}
																				/>
																			);

																		return (
																			<a
																				href="#"
																				onClick={async (e) => {
																					e.preventDefault();
																					if (
																						downloadDocumentMutation.isPending
																					)
																						return;

																					const res =
																						await downloadDocumentMutation.mutateAsync(
																							{ id: record.id, mppId },
																						);

																					const link =
																						document.createElement('a');
																					link.href = res.data.signedUrl;
																					document.body.appendChild(link);

																					link.click();
																					document.body.removeChild(link);
																				}}
																			>
																				{downloadDocumentMutation.isPending &&
																				downloadDocumentMutation.variables
																					.id === record.id ? (
																					<LoadingOutlined />
																				) : (
																					<FileSearchOutlined />
																				)}
																			</a>
																		);
																	},
																},
															]}
														/>
													</Fragment>
												))}
											</div>
										),
									},
								]
							: []),
					]}
				/>

				<Divider />

				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Approval History</FleetTitle>
				</div>
				<Table
					bordered
					pagination={false}
					dataSource={
						approvalHistory?.map((history, index) => ({
							...history,
							key: index,
						})) ?? []
					}
					columns={[
						{
							title: 'No',
							align: 'center',
							render: (_, __, index) => index + 1,
						},
						{
							title: 'Name',
							render: (_, record) => record.name,
						},
						{
							title: 'Date',
							align: 'center',
							render: (_, record) =>
								record.date && dayjs(record.date).format('DD/MM/YYYY'),
						},
						{
							title: 'Process',
							align: 'center',
							render: (_, record) => record.process,
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => record.status,
						},
						{
							title: 'Approval Notes',
							align: 'center',
							render: (_, record) => record.notes,
						},
					]}
				/>
				{groupName === 'FCOH' && (
					<Fragment>
						<Divider />
						<Form.Item
							name={fieldName('action')}
							label="Approval"
							rules={[rule.required()]}
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
						>
							<Select
								style={{ width: 200 }}
								options={approvalActions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
					</Fragment>
				)}

				{(groupName === 'FCOH' || groupName === 'FCDVS') && (
					<Fragment>
						<Divider />

						<Form.Item
							name={fieldName('notes')}
							rules={[rule.required()]}
							label="Notes"
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
						>
							<Input.TextArea rows={4} />
						</Form.Item>

						{!viewOnly && (
							<Button
								loading={isFormSubmitting}
								disabled={
									groupName === 'FCOH' && value?.purchaseOrderIds?.length === 0
								}
								type="primary"
								htmlType="submit"
								style={{
									display: 'block',
									width: 200,
									marginLeft: 'auto',
									marginTop: 40,
								}}
							>
								SAVE
							</Button>
						)}
					</Fragment>
				)}

				{groupName === 'FCAS' && (
					<Fragment>
						<Divider />

						<Form.Item
							label="Notes FCDVS"
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
						>
							<Input.TextArea
								rows={4}
								disabled
								value={fcdvsNotes}
							/>
						</Form.Item>

						<Button
							type="primary"
							style={{
								display: 'block',
								width: 200,
								marginLeft: 'auto',
								marginTop: 40,
							}}
							onClick={() => onBack?.()}
						>
							BACK
						</Button>
					</Fragment>
				)}
			</Form>
		);
	};

	const renderViewTransaction = () => {
		const data = view.type === 'view' ? view.data : undefined;
		if (!data) return null;

		const totalUnit =
			data?.assetInformations?.reduce(
				(acc, curr) => acc + curr.unitAmount,
				0,
			) ?? 0;

		// Calculate total NTF from transaction data
		const totalNtf =
			transactionDetailSummary?.reduce((acc, transaction) => {
				return acc + (transaction.totalNtf ?? 0);
			}, 0) ?? 0;

		// Calculate KKB reward based on totalNtf and percentage
		const kkbRewardPercentage = data.kkbRewardPercentage ?? 0;
		const totalKkbReward = (totalNtf * kkbRewardPercentage) / 100;

		const kkbRewardPerUnit =
			(totalUnit ?? 0) > 0 ? totalKkbReward / (totalUnit ?? 1) : 0;
		const kkbRewardAllUnit = totalKkbReward;

		const getDisabledRewardOrderSources = () => {
			return [
				{ id: 3, value: 'Captive JF Commercial' },
				// { id: 4, value: 'Captive JF Corporate' },
				// Note: Captive Non-JF (id: 2) should have rewards enabled
				// Note: Captive MTF (id: 5) should have rewards enabled
			];
		};

		// Check if current order source should have rewards disabled
		const shouldDisableReward = () => {
			if (!orderSource) return false;

			const disabledSources = getDisabledRewardOrderSources();
			return disabledSources.some(
				(source) =>
					source.id === orderSource.id || source.value === orderSource.value,
			);
		};

		const disableReward = shouldDisableReward();

		return (
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				labelCol={{ span: 10 }}
				wrapperCol={{ span: 14, style: { alignSelf: 'center' } }}
				disabled={true}
			>
				<Tabs
					activeKey={'1'}
					items={[
						{
							key: '1',
							label: 'Rincian Detail Transaksi',
							children: (
								<Fragment>
									<div style={{ marginBottom: 20 }}>
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item label="Jenis Pembiayaan">
													{data?.financingProduct?.value ?? '-'}
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item label="Jenis Asset">
													{data.assetType ?? '-'}
												</Form.Item>
											</Col>
										</Row>
										<Divider />
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item label="Segment Industri">
													{data?.segmentIndustry ?? '-'}
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item label="Tujuan Pembiayaan">
													<Input
														disabled
														value={
															financingPurpose?.find(
																(option) =>
																	option.id === data.financialPurposeId,
															)?.value ?? '-'
														}
													/>
												</Form.Item>
											</Col>
										</Row>
									</div>
								</Fragment>
							),
						},
					]}
				/>
				<Collapse
					defaultActiveKey={disableReward ? [0, 2] : [0, 1, 2]}
					items={[
						{
							label: 'Informasi Aset',
							children: (
								<Fragment>
									<Row
										align="middle"
										style={{ marginBottom: 20, gap: 40 }}
									>
										<Col flex="none">
											<strong>Total Jumlah Unit</strong>
										</Col>
										<Col flex="auto">
											<span>{totalUnit} Unit</span>
										</Col>
									</Row>
									<Form.Item wrapperCol={{ span: 24 }}>
										<Table
											bordered
											pagination={false}
											scroll={{ x: 'max-content' }}
											dataSource={
												data.assetInformations.map((record, key) => ({
													...record,
													key,
												})) ?? []
											}
											columns={[
												{
													title: 'No',
													align: 'center',
													width: 1,
													render: (_, __, index) => index + 1,
												},
												{
													title: 'Nama STNK/BPKB/Invoice',
													render: (_, record) => record.bpkbOwnerName ?? '-',
												},
												{
													title: 'Jumlah Unit',
													render: (_, record) => record.unitAmount ?? '-',
												},
												{
													title: 'Pengguna Aset',
													render: (_, record) => record.assetUser ?? '-',
												},
												{
													title: 'Nama Bouwheer',
													render: (_, record) => record.bouwheer?.name ?? '-',
												},
												{
													title: 'Bouwheer Type',
													render: (_, record) => record.bouwheer?.type ?? '-',
												},
												{
													title: 'Industri Bouwheer',
													render: (_, record) =>
														record.bouwheer?.industrySector?.sectorLevel5 ??
														'-',
												},
												{
													title: 'Kota',
													render: (_, record) =>
														record.address?.cityName ?? '-',
												},
											]}
										/>
									</Form.Item>
								</Fragment>
							),
						},
						{
							label: 'Reward',
							collapsible: disableReward ? 'disabled' : undefined,
							children: !disableReward && (
								<Fragment>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Apakah ada reward KKB?">
												<Input
													disabled
													value={
														data.kkbRewardAvailableId === 1
															? 'Ya'
															: data.kkbRewardAvailableId === 2
																? 'Tidak'
																: '-'
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Nama Pemberi Referral">
												{data.kkbReferralName ?? '-'}
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Apakah ada reward KKB?">
												<InputNumber
													disabled
													value={data.kkbRewardPercentage}
													suffix="%"
													controls={false}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
									</Row>

									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												label={
													<Fragment>
														<span>
															Jumlah Reward KKB (Rp)
															<br />
															<small style={{ fontWeight: 'normal' }}>
																Per Unit
															</small>
														</span>
													</Fragment>
												}
											>
												<Input
													disabled
													value={
														kkbRewardPerUnit
															? formatCurrency(kkbRewardPerUnit)
															: ''
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label={
													<Fragment>
														<span>
															Jumlah Reward KKB (Rp)
															<br />
															<small style={{ fontWeight: 'normal' }}>
																All Unit
															</small>
														</span>
													</Fragment>
												}
											>
												<Input
													disabled
													value={
														kkbRewardAllUnit
															? formatCurrency(kkbRewardAllUnit)
															: ''
													}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Fragment>
							),
						},
						{
							label: 'Cara Pembayaran Angsuran',
							children: (
								<Fragment>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Way Of Payment">
												<Input
													disabled
													value={data.wayOfPayment?.value ?? '-'}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Nama Bank">
												<Input
													value={data.bank?.value ?? '-'}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Nama Cabang">
												<Input
													disabled
													value={data.bankBranch ?? '-'}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Nomor Rekening">
												<Input
													value={data.bankAccountNumber ?? '-'}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Form.Item
										label="Notes"
										labelCol={{ span: 5 }}
										wrapperCol={{ span: 19 }}
									>
										<Input.TextArea
											rows={4}
											disabled
											value={data.wayOfPaymentNotes ?? '-'}
										/>
									</Form.Item>
									<Divider />
								</Fragment>
							),
						},
					]}
				/>
				<div
					style={{
						display: 'flex',
						gap: 20,
						justifyContent: 'flex-end',
						marginTop: 20,
					}}
				>
					<Button
						disabled={false}
						style={{
							display: 'block',
							width: 200,
						}}
						onClick={() => {
							setView({
								type: 'main',
							});
						}}
					>
						BACK
					</Button>
				</div>
			</Form>
		);
	};

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle>Main Information</FleetTitle>
				</div>
			</div>
			<DataRow
				style={{ marginBottom: 20 }}
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								'-'
							),
						],
						['No.MKL', mklNo],
					],
					[
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
						[
							'No.SPF',
							spfNo && spfId ? (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('SPFPage', {
											prospectId: prospectId!,
											spfId,
											napResolutionId: napResolutionId!,
											viewOnly: true,
											onBack: () => {
												navigate('DisbursementPage', {
													mppId,
													purchaseOrderIds,
													financingProductId,
													onBack,
												});
											},
										});
									}}
								>
									{spfNo}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						['Portofolio Guideline', portfolioGuideline],
						['Kelas Industri', industryClass],
					],
					[
						[
							'Summary NAP',
							napPlafonTypeId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) return;

										if (napPlafonTypeId === 1) {
											napNJFModal.open({
												prospectId,
											});
										}

										if (napPlafonTypeId === 2) {
											napJFModal.open({
												prospectId,
											});
										}
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
						['Ultimate Owner', ultimateOwner],
					],
					[
						[
							'View Kolektabilitas',
							slikInquiryId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										kolektabilitasModal.open({
											slikInquiryId,
										});
									}}
								>
									View Kolektabilitas
								</a>
							) : (
								'-'
							),
						],
						[
							'Summary Risalah',
							resolutionFile ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(resolutionFile);
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
					],
					groupName === 'FCDVS' || groupName === 'FCAS'
						? [
								['FCDVS ', fcdvsName ?? '-'],
								['FCAS ', fcasName ?? '-'],
							]
						: undefined,
				]}
			/>
			{renderMainForm()}
			{renderViewTransaction()}

			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			<CovenantModal
				{...covenantModal}
				covenantStatusOptions={covenantStatusOptions}
			/>
			<KolektabilitasModal {...kolektabilitasModal} />

			{/* Simulation Result Modal */}
			<SimulationResultModal
				open={simulationModalOpen}
				onClose={handleCloseSimulationModal}
				simulationId={selectedSimulation?.simulationId}
			/>
		</Fragment>
	);
};

export default DisbursementPage;
